import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import AuthGate from './AuthGate';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, profile, signOut, loading } = useAuth();

  // Update navbar style on scroll
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile & dropdown menus on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsDropdownOpen(false);
  }, [location.pathname]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('.user-dropdown')) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // Scroll to FAQ when hash changes
  useEffect(() => {
    if (location.hash === '#faq' && location.pathname === '/') {
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        const faqSection = document.getElementById('faq');
        if (faqSection) {
          faqSection.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  }, [location.hash, location.pathname]);

  const navLinks = [
    { name: 'Ürünler & Teknoloji', path: '/product' },
    { name: 'Fiyatlandırma', path: '/pricing' },
    { name: 'Terimler Sözlüğü', path: '/glossary' },
    { name: 'Kaynaklar', path: '/resources' },
    { name: 'SSS', path: '/#faq' },
  ];

  const handleNavClick = (e: React.MouseEvent, path: string) => {
    if (location.pathname === path) {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleLogoClick = (e: React.MouseEvent) => {
    if (location.pathname === '/') {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Scroll to FAQ section, handling both same‑page and different‑page navigation
  const scrollToFAQ = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsMobileMenuOpen(false);
    
    if (location.pathname !== '/') {
      // Navigate to home page first, then scroll will happen via useEffect
      navigate('/#faq');
    } else {
      const faqSection = document.getElementById('faq');
      if (faqSection) {
        faqSection.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const handleSignOut = async () => {
    try {
      setIsDropdownOpen(false);
      await signOut();
    } catch (err) {
      console.error('Sign‑out failed:', err);
    }
  };

  const handleAuthClick = (e: React.MouseEvent) => {
    if (location.pathname === '/auth') {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: 'smooth' });
      // Trigger login tab switch via custom event
      window.dispatchEvent(new CustomEvent('switchToLogin'));
    }
  };

  const displayName = profile?.full_name || user?.email?.split('@')[0] || '';
  const initials = displayName ? displayName.charAt(0).toUpperCase() : 'U';

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled || isMobileMenuOpen ? 'bg-dark/95 backdrop-blur-lg shadow-lg' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
        <div className="flex items-center justify-between">
          <Link to="/" onClick={handleLogoClick} className="flex items-center gap-2 sm:gap-3 group">
            <img
              src="https://public.readdy.ai/ai/img_res/f3044dfd-b61c-43a8-ab16-9fe4258f969f.png"
              alt="BAGA Logo"
              className="h-8 w-8 sm:h-10 sm:w-10 transition-transform group-hover:scale-110"
            />
            <span className="text-xl sm:text-2xl font-bold text-beige-light">BAGA</span>
          </Link>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => {
              if (link.path === '/#faq') {
                return (
                  <a
                    key={link.path}
                    href="#faq"
                    onClick={scrollToFAQ}
                    className="text-beige hover:text-emerald transition-colors text-sm font-medium whitespace-nowrap cursor-pointer"
                  >
                    {link.name}
                  </a>
                );
              }
              return (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={(e) => handleNavClick(e, link.path)}
                  className="text-beige hover:text-emerald transition-colors text-sm font-medium whitespace-nowrap"
                >
                  {link.name}
                </Link>
              );
            })}

            <AuthGate>
              {(handleClick) => (
                <button
                  onClick={handleClick}
                  className="bg-emerald hover:bg-emerald-light text-beige-light px-6 py-2.5 rounded-full text-sm font-semibold transition-all hover:shadow-lg hover:shadow-emerald/50 whitespace-nowrap cursor-pointer"
                >
                  Canlı Kararları Gör
                </button>
              )}
            </AuthGate>

            {/* Auth Area */}
            {!loading && (
              <>
                {user ? (
                  <div className="relative user-dropdown">
                    <button
                      onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                      className="flex items-center gap-2 cursor-pointer group"
                    >
                      <div className="w-9 h-9 rounded-full bg-emerald/20 border border-emerald/40 flex items-center justify-center text-emerald font-bold text-sm group-hover:bg-emerald/30 transition-all">
                        {initials}
                      </div>
                    </button>

                    {isDropdownOpen && (
                      <div className="absolute right-0 top-full mt-3 w-56 bg-dark-card border border-dark-border rounded-xl shadow-2xl shadow-black/40 overflow-hidden z-50">
                        <div className="px-4 py-3 border-b border-dark-border">
                          <p className="text-sm font-semibold text-beige-light truncate">{displayName}</p>
                          <p className="text-xs text-beige/50 truncate">{user.email}</p>
                        </div>
                        <div className="py-1">
                          <Link
                            to="/account"
                            className="flex items-center gap-3 px-4 py-2.5 text-sm text-beige/80 hover:text-beige-light hover:bg-dark-border/30 transition-colors cursor-pointer"
                          >
                            <i className="ri-user-line text-base"></i>
                            Hesabım
                          </Link>
                          <button
                            onClick={handleSignOut}
                            className="flex items-center gap-3 px-4 py-2.5 text-sm text-red-400 hover:text-red-300 hover:bg-dark-border/30 transition-colors w-full text-left cursor-pointer"
                          >
                            <i className="ri-logout-box-r-line text-base"></i>
                            Çıkış Yap
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <Link
                    to="/auth"
                    onClick={handleAuthClick}
                    className="border border-emerald/40 text-emerald hover:bg-emerald/10 px-5 py-2.5 rounded-full text-sm font-semibold transition-all whitespace-nowrap cursor-pointer"
                  >
                    Giriş Yap
                  </Link>
                )}
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center text-beige-light hover:text-emerald transition-colors cursor-pointer"
            aria-label="Menü"
          >
            <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden mt-4 pb-4 border-t border-gray-800 pt-4">
            <div className="flex flex-col gap-4">
              {navLinks.map((link) => {
                if (link.path === '/#faq') {
                  return (
                    <a
                      key={link.path}
                      href="#faq"
                      onClick={scrollToFAQ}
                      className="text-beige hover:text-emerald transition-colors text-base font-medium py-2 cursor-pointer"
                    >
                      {link.name}
                    </a>
                  );
                }
                return (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={(e) => handleNavClick(e, link.path)}
                    className="text-beige hover:text-emerald transition-colors text-base font-medium py-2"
                  >
                    {link.name}
                  </Link>
                );
              })}
              <AuthGate>
                {(handleClick) => (
                  <button
                    onClick={handleClick}
                    className="bg-emerald hover:bg-emerald-light text-beige-light px-6 py-3 rounded-full text-base font-semibold transition-all text-center whitespace-nowrap cursor-pointer mt-2 w-full"
                  >
                    Canlı Kararları Gör
                  </button>
                )}
              </AuthGate>

              {/* Mobile Auth */}
              {!loading && (
                <>
                  {user ? (
                    <div className="border-t border-gray-800 pt-4 mt-2 space-y-3">
                      <div className="flex items-center gap-3 px-1">
                        <div className="w-9 h-9 rounded-full bg-emerald/20 border border-emerald/40 flex items-center justify-center text-emerald font-bold text-sm">
                          {initials}
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-beige-light">{displayName}</p>
                          <p className="text-xs text-beige/50">{user.email}</p>
                        </div>
                      </div>
                      <Link
                        to="/account"
                        className="flex items-center gap-2 text-beige hover:text-emerald transition-colors text-base font-medium py-2 cursor-pointer"
                      >
                        <i className="ri-user-line"></i>Hesabım
                      </Link>
                      <button
                        onClick={handleSignOut}
                        className="flex items-center gap-2 text-red-400 hover:text-red-300 transition-colors text-base font-medium py-2 cursor-pointer"
                      >
                        <i className="ri-logout-box-r-line"></i>Çıkış Yap
                      </button>
                    </div>
                  ) : (
                    <Link
                      to="/auth"
                      onClick={handleAuthClick}
                      className="border border-emerald/40 text-emerald hover:bg-emerald/10 px-6 py-3 rounded-full text-base font-semibold transition-all text-center whitespace-nowrap cursor-pointer"
                    >
                      Giriş Yap
                    </Link>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
